import { useRef, useState, Dispatch, SetStateAction } from 'react'

import {
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControlLabel,
    IconButton,
    makeStyles,
    Radio,
    RadioGroup,
    TextField,
    Typography
} from '@material-ui/core'
import { Close as CloseIcon, FileCopy as FileCopyIcon } from '@material-ui/icons'

import { SpacedGroup } from 'UI/Components'

import { firebaseConfig } from 'env'
import { useNotification } from 'hooks'
import { copyToClipboard } from 'modules'

const useStyles = makeStyles(theme => ({
    title: {
        textAlign: 'center',
        fontWeight: theme.fontWeightBold
    },
    closeButton: {
        position: 'absolute',
        right: 0,
        top: 0,
        padding: theme.spacing(0.5),
        margin: theme.spacing(0.5)
    },
    code: {
        padding: theme.spacing(1),
        border: '1px solid gainsboro',
        borderRadius: '4px'
    },
    actions: {
        padding: theme.spacing(3),
        justifyContent: 'center'
    }
}))

const iframeType = {
    responsive: 'Responsive',
    fixed: 'Fixed'
}

type Props = {
    isOpen: boolean
    onClose: () => void
    title: string
    playbookId: string
    playbookTitle?: string
    isPlaylist?: boolean
}

export const CopyCodeDialog = ({
    isOpen,
    onClose,
    title,
    playbookId,
    playbookTitle = 'Guidde embed video',
    isPlaylist
}: Props) => {
    const classes = useStyles()
    const { showSuccessNotification } = useNotification()

    const codeRef = useRef<HTMLElement | null>(null)

    const [type, setType] = useState(iframeType.fixed)
    const [width, setWidth] = useState(824)
    const [height, setHeight] = useState(620)

    const handleSizeChange = (event: any, callback: Dispatch<SetStateAction<number>>) => {
        const onlyNums = event.target.value.replace(/[^0-9]/g, '')
        if (onlyNums.length < 5) callback(onlyNums)
    }

    const page = isPlaylist ? 'playlists' : 'playbooks'

    const iframeUrl = `https://embed.${firebaseConfig.authDomain}/${page}/${playbookId}`
    const iframeWidth = type === iframeType.responsive ? '100%' : width + 'px'
    const iframeHeight = type === iframeType.responsive ? '100%' : height + 'px'

    return (
        <Dialog open={isOpen} onClose={onClose} fullWidth maxWidth="sm">
            <IconButton aria-label="close" className={classes.closeButton} onClick={onClose}>
                <CloseIcon />
            </IconButton>

            <DialogTitle>
                <Typography className={classes.title}>{title}</Typography>
            </DialogTitle>
            <DialogContent>
                <Box className={classes.code}>
                    <code ref={codeRef}>
                        {`<iframe src=
                            "${iframeUrl}"
                            width="${iframeWidth}"
                            height="${iframeHeight}"
                            title="${playbookTitle}"
                            frameborder="0"
                            allowfullscreen></iframe>`}
                    </code>
                </Box>

                <Box mt={1}>
                    <RadioGroup row value={type} onChange={(_, value) => setType(value)}>
                        <FormControlLabel
                            value={iframeType.responsive}
                            control={<Radio color="primary" />}
                            label={iframeType.responsive}
                        />
                        <FormControlLabel
                            value={iframeType.fixed}
                            control={<Radio color="primary" />}
                            label={iframeType.fixed}
                        />
                    </RadioGroup>
                </Box>

                {type === iframeType.fixed && (
                    <Box mt={1}>
                        <SpacedGroup>
                            <TextField
                                value={width}
                                onChange={e => handleSizeChange(e, setWidth)}
                                variant="outlined"
                                size="small"
                                style={{ width: '75px' }}
                            />
                            <Typography>X</Typography>
                            <TextField
                                value={height}
                                onChange={e => handleSizeChange(e, setHeight)}
                                variant="outlined"
                                size="small"
                                style={{ width: '75px' }}
                            />
                            <Typography>Pixels</Typography>
                        </SpacedGroup>
                    </Box>
                )}
            </DialogContent>
            <DialogActions className={classes.actions}>
                <Button
                    startIcon={<FileCopyIcon />}
                    onClick={() => {
                        copyToClipboard(codeRef?.current?.innerText).then(() => {
                            showSuccessNotification('Code was copied to the clipboard')
                        })
                    }}
                >
                    Copy Embed Code
                </Button>
            </DialogActions>
        </Dialog>
    )
}
